import { theme } from "antd";
import type { IMetricBlock, IMetricTile } from "./chat-block.types";

type Props = {
	block: IMetricBlock;
};

// A row of KPI tiles. Values and deltas are preformatted strings from the
// emitter; the renderer only colors the delta by its semantic `tone` (up = good,
// down = bad, warn = attention). The emitter decides tone, so the renderer stays
// domain-agnostic — an expense increase can be sent as `warn`, not `up`.
export function MetricBlock({ block }: Props) {
	const { token } = theme.useToken();
	const columns = Math.min(block.tiles.length, 3);

	const deltaColor = (tone: IMetricTile["tone"]): string => {
		switch (tone) {
			case "up":
				return token.colorSuccess;
			case "down":
				return token.colorError;
			case "warn":
				return token.colorWarning;
			default:
				return token.colorTextSecondary;
		}
	};

	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
				gap: 10,
			}}
		>
			{block.tiles.map((tile) => (
				<div
					key={`${tile.label}-${tile.value}`}
					style={{
						position: "relative",
						background: token.colorBgContainer,
						border: `1px solid ${token.colorBorder}`,
						borderRadius: token.borderRadiusLG,
						padding: "13px 15px 14px 17px",
						overflow: "hidden",
					}}
				>
					{/* brand edge-stripe: green as a disciplined accent, not a fill */}
					<div
						style={{
							position: "absolute",
							left: 0,
							top: 0,
							bottom: 0,
							width: 3,
							background: `linear-gradient(180deg, ${token.colorPrimary} 0%, var(--color-primary-hover) 100%)`,
						}}
					/>
					<div
						style={{
							fontSize: 10.5,
							fontWeight: 700,
							letterSpacing: "0.04em",
							textTransform: "uppercase",
							color: token.colorTextSecondary,
						}}
					>
						{tile.label}
					</div>
					<div
						style={{
							fontSize: 24,
							fontWeight: 700,
							marginTop: 6,
							lineHeight: 1,
							letterSpacing: "-0.01em",
							fontVariantNumeric: "tabular-nums",
							color: token.colorText,
						}}
					>
						{tile.value}
					</div>
					{tile.delta ? (
						<div
							style={{
								fontSize: 11,
								marginTop: 6,
								color: deltaColor(tile.tone),
							}}
						>
							{tile.delta}
						</div>
					) : null}
				</div>
			))}
		</div>
	);
}
